import { useEffect, useState } from 'react';
import {
  Alert,
  Flex,
  FlexColumn,
  Link,
} from '@elementary/common-ui/components';
import { CircularProgress, Typography } from '@mui/material';
import { FivetranDestinationGroups } from 'shared/types';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import FivetranCredentialsSection from './FivetranCredentialsSection';
import FivetranDestinationGroupsStep from './FivetranDestinationGroupsStep';
import useFivetranDialog from './useFivetranDialog';

const FIVETRAN_API_SETTINGS_URL =
  'https://fivetran.com/docs/rest-api/getting-started';

type Props = {
  open: boolean;
  onClose: () => void;
  envId: string;
  fivetranIntegrationId?: string;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) return undefined;
  const apiMsg = (error as any)?.response?.data?.error_message as
    | string
    | undefined;
  if (apiMsg) return apiMsg;
  if (error instanceof Error) return error.message;
  return undefined;
};

const FivetranDialog = ({
  open,
  onClose,
  envId,
  fivetranIntegrationId,
}: Props) => {
  const isEdit = !!fivetranIntegrationId;

  const { configQuery, connectMutation, saveMutation } = useFivetranDialog({
    envId,
    integrationId: fivetranIntegrationId,
    isOpen: open,
  });

  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [isEditInitialized, setIsEditInitialized] = useState(false);
  const [credentialsChangedSinceConnect, setCredentialsChangedSinceConnect] =
    useState(false);
  const [initialFetchData, setInitialFetchData] = useState<
    FivetranDestinationGroups | undefined
  >(undefined);
  const [initialFetchError, setInitialFetchError] = useState<unknown>(null);

  const config = configQuery.data;
  const isM2 = config?.has_credentials === true;
  const isM1 = isEdit && config?.has_credentials === false;

  const bothFieldsEmpty = !apiKey && !apiSecret;
  const showStoredCredsState = isM2 && bothFieldsEmpty;

  const activeGroupsData = showStoredCredsState
    ? initialFetchData
    : connectMutation.data;
  const activeConnectError = showStoredCredsState
    ? initialFetchError
    : connectMutation.isError
    ? connectMutation.error
    : null;

  const groups = activeGroupsData?.destination_groups ?? [];
  const lockedIds = groups.filter((g) => g.is_selected).map((g) => g.id);
  const hasGroups = !!activeGroupsData && !credentialsChangedSinceConnect;

  const isLoadingConfig =
    isEdit && (configQuery.isLoading || configQuery.isFetching);

  useEffect(() => {
    if (!open || isEditInitialized || !config || configQuery.isFetching) {
      return;
    }
    if (isM2) {
      connectMutation.mutate(null, {
        onSuccess: (data) => setInitialFetchData(data),
        onError: (error) => setInitialFetchError(error),
      });
    }
    setIsEditInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config, isEditInitialized, configQuery.isFetching]);

  useEffect(() => {
    if (!connectMutation.data) return;
    const newGroups = connectMutation.data.destination_groups;
    setSelectedGroupIds(
      newGroups.filter((g) => g.is_selected).map((g) => g.id),
    );
  }, [connectMutation.data]);

  useEffect(() => {
    if (!open) {
      setApiKey('');
      setApiSecret('');
      setSelectedGroupIds([]);
      setIsEditInitialized(false);
      setCredentialsChangedSinceConnect(false);
      setInitialFetchData(undefined);
      setInitialFetchError(null);
      connectMutation.reset();
      saveMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const restoreStoredSelection = (data: FivetranDestinationGroups) => {
    setSelectedGroupIds(
      data.destination_groups.filter((g) => g.is_selected).map((g) => g.id),
    );
  };

  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    connectMutation.reset();
    saveMutation.reset();
    const bothEmpty = !value && !apiSecret;
    setCredentialsChangedSinceConnect(!bothEmpty);
    if (bothEmpty && initialFetchData) restoreStoredSelection(initialFetchData);
  };

  const handleApiSecretChange = (value: string) => {
    setApiSecret(value);
    connectMutation.reset();
    saveMutation.reset();
    const bothEmpty = !apiKey && !value;
    setCredentialsChangedSinceConnect(!bothEmpty);
    if (bothEmpty && initialFetchData) restoreStoredSelection(initialFetchData);
  };

  const handleConnect = () => {
    saveMutation.reset();
    setCredentialsChangedSinceConnect(false);
    connectMutation.mutate({
      apiKey: apiKey.trim(),
      apiSecret: apiSecret.trim(),
    });
  };

  const handleSave = () => {
    const trimmedKey = apiKey.trim();
    const trimmedSecret = apiSecret.trim();
    saveMutation.mutate(
      {
        destinationGroupIds: selectedGroupIds,
        ...(trimmedKey && { apiKey: trimmedKey }),
        ...(trimmedSecret && { apiSecret: trimmedSecret }),
      },
      { onSuccess: onClose },
    );
  };

  const savedGroupIds: string[] =
    isM2 && config?.has_credentials ? config.destination_group_ids : [];
  const isGroupSelectionChanged =
    selectedGroupIds.length !== savedGroupIds.length ||
    selectedGroupIds.some((id) => !savedGroupIds.includes(id));
  const isDirty = !isEdit || isGroupSelectionChanged || !!apiKey || !!apiSecret;

  const connectErrorMessage: string | undefined = activeConnectError
    ? showStoredCredsState
      ? getErrorMessage(activeConnectError) ??
        "Couldn't connect with stored credentials. Enter a new API key and secret and click Connect."
      : getErrorMessage(activeConnectError)
    : undefined;

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Fivetran connection' : 'Connect Fivetran'}
      description="Elementary monitors your Fivetran destination groups and creates incidents when syncs fail."
      width={560}
      saveButtonText={isEdit ? 'Save changes' : 'Connect'}
      onSubmit={handleSave}
      isValid={
        !isLoadingConfig && hasGroups && selectedGroupIds.length > 0 && isDirty
      }
      loading={saveMutation.isLoading}
    >
      {isLoadingConfig ? (
        <Flex sx={{ justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Flex>
      ) : (
        <FlexColumn sx={{ gap: 3 }}>
          {isM1 && (
            <Alert severity="info" title="Reconnect required">
              Fivetran now connects with an API key and secret. Add yours below
              to pick which destination groups Elementary monitors.
            </Alert>
          )}

          <Typography>
            Enter your Fivetran API key and secret so Elementary can list your
            destination groups. You can create them from your{' '}
            <Link
              href={FIVETRAN_API_SETTINGS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fivetran API settings
            </Link>
            .
          </Typography>

          <FivetranCredentialsSection
            apiKey={apiKey}
            apiSecret={apiSecret}
            onApiKeyChange={handleApiKeyChange}
            onApiSecretChange={handleApiSecretChange}
            onConnect={handleConnect}
            isConnecting={connectMutation.isLoading}
            isConnected={hasGroups}
            errorMessage={connectErrorMessage}
            apiKeyPlaceholder={
              isM2 ? 'Enter a new API key' : 'Enter your API key'
            }
            apiSecretPlaceholder={
              isM2 ? 'Enter a new API secret' : 'Enter your API secret'
            }
          />

          {hasGroups && (
            <FivetranDestinationGroupsStep
              groups={groups}
              selectedIds={selectedGroupIds}
              lockedIds={lockedIds}
              onChange={(ids) => {
                saveMutation.reset();
                setSelectedGroupIds(ids);
              }}
              errorMessage={getErrorMessage(saveMutation.error)}
            />
          )}
        </FlexColumn>
      )}
    </IntegrationDialog>
  );
};

export default FivetranDialog;
